import { useEffect, useState, useCallback } from "react";
import { api, notifyApiError, INR } from "../lib/api";
import { toast } from "sonner";
import { XCircle, CheckCircle2, RefreshCcw } from "lucide-react";

/**
 * Compact "Unpaid Sessions" panel.
 * Used by both Admin (Dashboard) and Owner consoles.
 *
 * Backend:
 *   GET   /api/sessions/unpaid          → list
 *   PATCH /api/sessions/{id}/payment    → mark paid / change mode
 */
export default function UnpaidSessionsPanel({ refreshSignal = 0, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sessions/unpaid");
      setRows(data || []);
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  const handlePaid = (id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    // Notify any portal-aware widget (PlayersPortalCard / open PortalModal)
    // so the player's outstanding-dues + unpaid counters drop immediately.
    try { window.dispatchEvent(new CustomEvent("portal:refresh")); } catch { /* ignore */ }
    onChanged?.();
  };

  const total = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);

  return (
    <section
      data-testid="unpaid-panel"
      className="rounded-2xl border border-rose-400/20 bg-gradient-to-br from-rose-950/30 to-black/40 backdrop-blur p-4 sm:p-5"
    >
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <XCircle className="w-4 h-4 text-rose-300" />
          <h2 className="text-xs uppercase tracking-[0.25em] text-rose-200 font-bold">
            Unpaid Sessions
          </h2>
          <span
            data-testid="unpaid-count"
            className="text-[10px] uppercase tracking-wider bg-rose-400/20 text-rose-100 px-2 py-0.5 rounded-full font-bold"
          >
            {rows.length}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {rows.length > 0 && (
            <span
              className="text-amber-300 font-mono font-bold text-sm tabular-nums"
              data-testid="unpaid-total"
            >
              {INR(total)} due
            </span>
          )}
          <button
            data-testid="unpaid-refresh"
            onClick={load}
            className="text-gray-400 hover:text-amber-300 transition"
            title="Refresh"
          >
            <RefreshCcw className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-gray-500 text-xs py-3">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          data-testid="unpaid-empty"
          className="flex items-center gap-2 text-emerald-300/80 text-sm py-3"
        >
          <CheckCircle2 className="w-4 h-4" /> All clear — no unpaid sessions.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm" data-testid="unpaid-table">
            <thead className="text-gray-400 text-[10px] uppercase tracking-[0.2em]">
              <tr>
                <th className="text-left px-2 py-2">Player</th>
                <th className="text-left px-2 py-2">Table</th>
                <th className="text-left px-2 py-2">Ended</th>
                <th className="text-right px-2 py-2">Amount</th>
                <th className="text-right px-2 py-2">Mark Paid (mode)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <UnpaidRow key={s.id} s={s} onPaid={() => handlePaid(s.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function UnpaidRow({ s, onPaid }) {
  const [mode, setMode] = useState(s.payment_mode || "cash");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/sessions/${s.id}/payment`, {
        payment_status: "paid",
        payment_mode: mode,
      });
      toast.success(`Marked paid — ${s.player_name} · ${INR(s.amount)}`);
      onPaid();
    } catch (e) {
      notifyApiError(toast, e);
      setSaving(false);
    }
  };

  const ended = s.end_time
    ? new Date(s.end_time).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "—";

  return (
    <tr
      data-testid={`unpaid-row-${s.id}`}
      className="border-t border-white/5 hover:bg-white/[0.03]"
    >
      <td className="px-2 py-2.5">
        <span className="font-bold text-white">{s.player_name}</span>{" "}
        <span
          className={`ml-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
            s.player_type === "member"
              ? "bg-amber-400/15 text-amber-200"
              : s.player_type === "cross_club_member"
              ? "bg-sky-400/15 text-sky-200"
              : "bg-white/10 text-gray-300"
          }`}
        >
          {s.player_type === "member" ? "M" : s.player_type === "cross_club_member" ? "CCM" : "NM"}
        </span>
      </td>
      <td className="px-2 py-2.5 text-gray-300">{s.table_name}</td>
      <td className="px-2 py-2.5 text-gray-500 font-mono text-xs">{ended}</td>
      <td className="px-2 py-2.5 text-right font-mono font-bold text-amber-300 tabular-nums">
        {INR(s.amount)}
      </td>
      <td className="px-2 py-2.5">
        <div className="flex items-center justify-end gap-2">
          <select
            data-testid={`unpaid-mode-${s.id}`}
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            disabled={saving}
            className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-amber-400/40"
          >
            <option value="cash">Cash</option>
            <option value="upi">UPI</option>
            <option value="card">Card</option>
          </select>
          <button
            data-testid={`unpaid-mark-paid-${s.id}`}
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-1 rounded bg-emerald-500/90 hover:bg-emerald-400 disabled:opacity-50 text-black text-xs font-bold uppercase tracking-wider transition"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            {saving ? "Saving…" : "Paid"}
          </button>
        </div>
      </td>
    </tr>
  );
}
